<template>
	<div
		class="table-container pronto-table"
		:style="cssVars"
		:class="{ clickable, loading: loadingOverlay, bordered }"
	>
		<div v-if="!hideHeader" class="table-header-container">
			<div class="table-row table-header">
				<div
					v-if="selection"
					class="table-cell selector"
					@click="$emit('selection-all')"
				>
					<input
						v-if="showSelectAll"
						type="checkbox"
						:checked="allSelected"
						:disabled="disableSelection"
					/>
				</div>
				<div
					v-for="(col, i) in cols"
					class="table-cell"
					:ref="`col-${i}`"
					:key="col.field"
					:style="col.styles"
					:class="col.headerClasses"
					@click="sortByColumn(col)"
				>
					<slot :name="`column-${col.field}`" :data="col">
						{{ localize && col.label ? $t(col.label) : col.label }}
					</slot>
					<div
						class="table-sorter"
						:class="{
							active: sort.field === col.field,
							asc: sort.order === ASC,
							dsc: sort.order === DSC,
						}"
						v-if="col.sorter"
					>
						<CaretIcon up />
						<CaretIcon down />
					</div>
				</div>
			</div>
		</div>

		<div class="table-body" :class="{ scroll: scrollable && !infinite }">
			<RecycleScroller
				v-if="infinite"
				:items="list"
				:itemSize="rowHeight"
				keyField="key"
				ref="scroller"
				@resize="debouncedOnResize"
				@scroll="onScrollDebounced"
			>
				<template v-slot="{ item, index }">
					<TableRow
						:columns="cols"
						:item="item"
						:index="index"
						:selection="selection"
						:selected="
							selection && selectedKeys[item.key]
								? selectedKeys[item.key]
								: false
						"
						:selectOnRowClick="selectOnRowClick"
						:disableAdditionalSelection="disableAdditionalSelection"
						@selection="toggleSelection(item)"
						@row-click="$emit('row-click', $event)"
					>
						<template v-slot="{ column }">
							<slot
								:name="`cell-${column.field}`"
								:data="item.data"
								:value="item.data[column.field]"
								:selected="
									selection && selectedKeys[item.key]
										? selectedKeys[item.key]
										: false
								"
							>
								{{ item.data[column.field] }}
							</slot>
						</template>
					</TableRow>
				</template>
				<template #after>
					<div v-if="showEmptyState" class="table-empty-state">
						<slot name="error" v-if="error">
							<GenericErrorState @retry="$emit('retry')" />
						</slot>
						<slot name="empty" v-if="!error && list.length === 0">
							{{ localizedEmptyText }}
						</slot>
					</div>
					<div ref="loader">
						<div class="table-loading-more" v-if="loadingMore">
							<IosSpinner />
						</div>
					</div>
				</template>
			</RecycleScroller>

			<div v-if="!infinite" class="table-rows">
				<TableRow
					:columns="cols"
					:item="item"
					:index="index"
					:selection="selection"
					:selected="
						selection && selectedKeys[item.key] ? selectedKeys[item.key] : false
					"
					:selectOnRowClick="selectOnRowClick"
					:disableAdditionalSelection="disableAdditionalSelection"
					@selection="toggleSelection(item)"
					@row-click="$emit('row-click', $event)"
					v-for="(item, index) in list"
					:key="item.key"
				>
					<template v-slot="{ column }">
						<slot
							:name="`cell-${column.field}`"
							:data="item.data"
							:value="item.data[column.field]"
							:selected="
								selection && selectedKeys[item.key]
									? selectedKeys[item.key]
									: false
							"
						>
							{{ item.data[column.field] }}
						</slot>
					</template>
				</TableRow>
				<div v-if="showEmptyState" class="table-empty-state">
					<slot name="empty">
						{{ emptyText }}
					</slot>
				</div>
			</div>

			<div v-if="loadingOverlay" class="table-loading-overlay">
				<SkeletonBar
					class="table-row-skeleton"
					v-for="id in skeletons"
					:key="id"
					double
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { CaretIcon } from '@/icons'
import IosSpinner from '../../IosSpinner'
import debounce from 'debounce'
import { closeAllPopovers } from '../Popover'
import { closeAllDropdowns } from '../Dropdown'
import SkeletonBar from '../SkeletonBar'
import GenericErrorState from '../GenericErrorState'
import TableRow, { SELECTION_FIXED } from './TableRow'

export const ASC = 'ascending'
export const DSC = 'descending'

const getScrollbarWidth = () => {
	const div = document.createElement('div')
	div.style.overflow = 'scroll'
	div.style.position = 'absolute'
	document.body.appendChild(div)
	const width = div.offsetWidth - div.clientWidth
	document.body.removeChild(div)
	return width
}

const mapColumns = (cols) => {
	if (!cols || !cols.length) return cols
	return cols.map((col, i) => {
		return Object.assign({}, col, {
			styles: {
				// this sets the width of the column header
				'flex-basis': col.width || col.minWidth || '100%',
				'min-width': col.minWidth || '25px',
			},
			cellClasses: {
				centered: col.align === 'center',
				right: col.align === 'right',
				// this col-{index} class gets used to set the column width
				[`table-col-${i}`]: true,
			},
			headerClasses: {
				centered: col.align === 'center',
				right: col.align === 'right',
				sortable: !!col.sorter,
			},
		})
	})
}

const mapRows = (rows, keyField) => {
	if (!rows || !rows.length) return []
	return rows.slice().map((data) => ({
		key: data[keyField],
		data,
	}))
}

export default {
	name: 'Table',
	components: {
		RecycleScroller,
		CaretIcon,
		IosSpinner,
		TableRow,
		SkeletonBar,
		GenericErrorState,
	},
	props: {
		columns: Array,
		rows: Array,
		selection: { type: Boolean, default: null },
		showSelectAll: { type: Boolean, default: null },
		loadingOverlay: { type: Boolean, default: null },
		loadingMore: { type: Boolean, default: null },
		localize: { type: Boolean, default: null },
		infinite: { type: Boolean, default: null },
		scrollable: { type: Boolean, default: null },
		clickable: { type: Boolean, default: null },
		error: { type: Boolean, default: null },
		bordered: { type: Boolean, default: null },
		selectOnRowClick: { type: Boolean, default: null },
		closePopoversOnScroll: { type: Boolean, default: null },
		defaultSort: Object,
		hideHeader: { type: Boolean, default: null },
		// headerSize: String, // not implemented
		disableAdditionalSelection: { type: Boolean, default: null },
		selectedKeys: {
			type: Object,
			default: () => ({}),
		},
		keyField: {
			type: String,
			default: 'id',
		},
		height: {
			type: [Number, String],
			default: 500,
		},
		emptyText: {
			type: String,
			default: '',
		},
		loadMoreTrigger: {
			type: String,
			default: '300px',
		},
		rowHeight: {
			type: Number,
			default: 63,
		},
	},
	data() {
		return {
			cols: mapColumns(this.columns || []),
			list: mapRows(this.rows, this.keyField),
			sort: {
				field: this.defaultSort?.field ?? null,
				order: this.defaultSort?.order ?? ASC,
			},
			hasOverflown: false,
			ASC,
			DSC,
			cssVars: {
				'--scrollbar-width': getScrollbarWidth() + 'px',
				'--table-height': isNaN(this.height) ? this.height : `${this.height}px`,
				'--cell-height': this.rowHeight - 1 + 'px',
			},
			debouncedOnResize: debounce(this.onResize.bind(this), 10),
			onScrollDebounced: debounce(this.onScroll, 25, true),
			debouncedOnIntersection: debounce(
				this.onIntersection.bind(this),
				100,
				true
			),
			skeletons: Array.from(Array(25).keys()),
			SELECTION_FIXED,
		}
	},
	emits: [
		'selection-all',
		'retry',
		'sort',
		'selection',
		'load-more',
		'scroll',
		'row-click',
	],
	mounted() {
		this.initScrollObserver()
	},
	beforeUnmount() {
		this.observer?.disconnect()
		this.bodyResizeObserver?.disconnect()
	},
	activated() {
		if (this.keepAliveScrollPosition) {
			this.scrollTo(this.keepAliveScrollPosition)
		}
	},
	deactivated() {
		this.keepAliveScrollPosition = this.currentScrollPosition
	},
	watch: {
		rows: {
			handler() {
				this.list = mapRows(this.rows, this.keyField)
			},
			deep: true,
		},

		infinite(enabled) {
			if (enabled) {
				this.initScrollObserver()
			} else {
				this.observer?.disconnect()
			}
		},
		height(height) {
			this.cssVars['--table-height'] = isNaN(height) ? height : `${height}px`
		},
		columns(columns) {
			this.cols = mapColumns(columns || [])
			this.onResize()
		},
	},
	methods: {
		sortByColumn(col) {
			if (!col.sorter) return
			if (this.sort.field === col.field) {
				return this.reverseSort(col)
			}
			this.newSort(col)
		},
		reverseSort(col) {
			this.toggleSortOrder()
			if (col.sorter === true) {
				// sorting is happening remotely
				this.$emit('sort', this.sort, col)
			} else {
				this.list.reverse()
			}
		},
		newSort(col) {
			this.sort.field = col.field
			this.sort.order = ASC
			if (col.sorter === true) {
				return this.$emit('sort', this.sort, col)
			}
			this.list = this.list.slice().sort((a, b) => {
				return col.sorter(a.data, b.data)
			})
		},
		toggleSortOrder() {
			this.sort.order = this.sort.order === ASC ? DSC : ASC
		},
		toggleSelection(row) {
			this.$emit('selection', row.data)
		},
		onIntersection(entries) {
			if (!this.hasOverflown) {
				this.hasOverflown = true
				return
			}
			entries[0].isIntersecting && this.$emit('load-more')
		},
		async initScrollObserver() {
			if (!this.infinite || this.observer || !this.$refs.scroller?.$el) return
			await this.$nextTick()
			this.observer = new IntersectionObserver(this.debouncedOnIntersection, {
				root: this.$refs.scroller.$el,
				rootMargin: this.loadMoreTrigger,
				threshold: 0,
			})
			this.observer.observe(this.$refs.loader)
		},
		scrollTo(pos = 0) {
			if (!this.$refs.scroller?.$el) return
			this.$refs.scroller.$el.scrollTop = pos
		},
		scrollToTop() {
			this.scrollTo(0)
		},
		onResize() {
			requestAnimationFrame(() => {
				this.cols.forEach((c, i) => {
					const [ref] = this.$refs[`col-${i}`] || []
					if (!ref) return
					this.cssVars[`--col-${i}-width`] = `${ref.offsetWidth}px`
				})
			})
		},
		onScroll(e) {
			this.$emit('scroll', e)
			this.currentScrollPosition = e.target.scrollTop
			if (this.closePopoversOnScroll) {
				closeAllPopovers()
				closeAllDropdowns()
			}
		},
	},
	computed: {
		allSelected() {
			return (
				this.list.length > 0 && this.list.every((x) => this.selectedKeys[x.key])
			)
		},
		showEmptyState() {
			return !this.loadingOverlay && (this.error || this.list.length === 0)
		},
		localizedEmptyText() {
			return this.emptyText || this.$t('common_loader_noresults_title')
		},
	},
	SELECTION_FIXED,
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$header-height: 35px;

.table-container {
	width: 100%;
	&.clickable {
		.table-body {
			.table-row {
				cursor: pointer;
				&.disabled {
					cursor: not-allowed;
				}
			}
		}
	}
	&.loading {
		.table-body .table-row {
			opacity: 0;
		}
	}
	&.bordered {
		border: 1px solid var(--neutral-200);
		border-radius: 6px;
		overflow: hidden;
		.table-header-container {
			.table-header {
				border-top: 0;
			}
		}
		.vue-recycle-scroller__item-view {
			&:last-child {
				.table-row {
					border-bottom: 0;
				}
			}
		}
	}
	.table-body {
		width: 100%;
		position: relative;
		height: var(--table-height);
		background: white;
		:deep(.vue-recycle-scroller) {
			height: var(--table-height);
		}
		&.scroll {
			overflow-y: auto;
		}
	}
	.table-header-container {
		width: 100%;
		background: var(--neutral-100);
		border-bottom: 1px solid var(--neutral-200);
		border-radius: 6px 6px 0 0;
		.table-sorter {
			@include flex-column-center;
			opacity: 0;
			margin-left: 8px;
			&.active {
				opacity: 1;
				&.asc {
					:deep(.up) {
						color: #171c26;
					}
				}
				&.dsc {
					:deep(.down) {
						color: #171c26;
					}
				}
			}
			.pronto-icon {
				font-size: 12px;
				color: var(--neutral-400);
				margin: -3px 0;
			}
		}
	}
	:deep(.table-row) {
		width: 100%;
		display: flex;
		border-bottom: 1px solid var(--neutral-200);
		font-family: Avenir-Roman;
		&.table-header {
			border-top: 1px solid var(--neutral-200);
			border-bottom: 0;
			width: calc(100% - #{var(--scrollbar-width)});
			.table-cell {
				font-size: 14px;
				color: #7d7d7d;
				height: $header-height;
				min-height: $header-height;
				> svg {
					width: 10px;
					height: 14px;
					margin-left: 8px;
				}
				&.sortable {
					cursor: pointer;
					&:hover {
						background: #eee;
					}
				}
				&:hover {
					.table-sorter {
						opacity: 1;
					}
				}
			}
		}
		&.fixed-selection {
			cursor: default !important;
			.table-cell-content {
				opacity: 0.6;
			}
		}
		.table-cell {
			text-align: left;
			padding: 4px 16px;
			display: flex;
			align-items: center;
			color: var(--neutral-800);
			overflow: hidden;
			min-height: var(--cell-height);
			font-family: inherit;
			&.centered {
				justify-content: center;
				.table-cell-content {
					text-align: center;
				}
			}
			&.right {
				justify-content: flex-end;
				.table-cell-content {
					text-align: right;
				}
			}
			&.selector {
				cursor: pointer;
				flex-basis: 36px;
				padding-right: 0;
				font-size: 20px;
			}
			.table-cell-content {
				word-break: break-all;
				@include clamp(2);
				width: 100%;
			}
		}
		&:hover:not(.table-header) {
			background: $light-blue;
		}
	}
	.table-loading-more {
		width: 100%;
		:deep(.spinner-div) {
			position: static;
			margin: 20px auto;
		}
	}

	.table-loading-overlay {
		@include absolute-cover;
		@include flex-column;
		z-index: 1;
		padding: 16px;
		overflow: hidden;
	}

	.table-empty-state {
		@include absolute-cover;
		@include flex-center;
		z-index: 1;
	}

	.table-row-skeleton {
		height: 55px;
		margin-bottom: 16px;
		flex-shrink: 0;
		&:last-child {
			margin-bottom: 0;
		}
	}

	@for $i from 0 through 20 {
		.table-col-#{$i} {
			max-width: var(--col-#{$i}-width);
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.table-container {
		.table-body {
			background: var(--neutral-200);
		}
		.table-row {
			border-bottom: 1px solid var(--neutral-300);
			.table-cell {
				color: var(--neutral-800);
			}
			&:hover {
				background: var(--neutral-0);
			}
		}
		.table-loading-more {
			.ios-spinner {
				background: var(--neutral-200) !important;
			}
		}
	}
}
</style>
